import { useEffect, useState } from "react";
import { useLiveCampaigns } from "@/lib/promo/useCampaigns";
import { formatEndDateLong } from "@/lib/promo/inaugural";

/**
 * Scrolling launch-pricing strip above the header, with a live countdown.
 *
 * The deadline is NOT hardcoded — it is read from the same `promo_campaigns`
 * rows that decide eligibility and that the server enforces at payment. When
 * every campaign has expired or been switched off, the strip disappears on
 * its own (the campaign hook re-evaluates on a timer, so an open page updates
 * without a refresh).
 */
function pad(n: number) {
  return String(n).padStart(2, "0");
}

/** Ticks once a second, client-side only so SSR and hydration agree. */
function useCountdown(expiresAtIso: string) {
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    const target = Date.parse(expiresAtIso);
    if (!Number.isFinite(target)) return;
    const tick = () => setRemaining(Math.max(0, target - Date.now()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAtIso]);

  if (remaining === null) return null;
  const totalSeconds = Math.floor(remaining / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

export function InauguralPriceBanner() {
  const live = useLiveCampaigns();

  // Latest end time across the live campaigns drives the shared message.
  // Hooks must run unconditionally, so fall back to an empty target.
  const last = live.length
    ? live.reduce((a, b) => (Date.parse(a.expiresAt) >= Date.parse(b.expiresAt) ? a : b))
    : null;
  const countdown = useCountdown(last?.expiresAt ?? "");

  if (!live.length || !last) return null;

  const clock = countdown
    ? `${countdown.days}d ${pad(countdown.hours)}h ${pad(countdown.minutes)}m ${pad(countdown.seconds)}s`
    : null;

  const message = (
    <>
      <span>Inaugural prices</span>
      <i aria-hidden className="dot" />
      <span>💳 5% off on prepaid orders</span>
      <i aria-hidden className="dot" />
      <span>🚚 Free shipping on all orders</span>
      <i aria-hidden className="dot" />
      <span>Valid till {formatEndDateLong(last)}</span>
      {clock ? (
        <>
          <i aria-hidden className="dot" />
          <span className="tabular-nums">Ends in {clock}</span>
        </>
      ) : null}
    </>
  );

  return (
    <>
      {/* Spacer keeps page content below the fixed strip */}
      <div aria-hidden className="h-8 w-full md:h-9" />
      <div
        role="region"
        aria-label="Inaugural pricing"
        className="fixed inset-x-0 top-0 z-50 h-8 w-full overflow-hidden bg-foreground text-background md:h-9"
      >
        <div className="flex h-full items-center">
          <div className="promo-track promo-track--banner">
            {message}
            <i aria-hidden className="dot" />
            {/* Duplicate copy makes the scroll seamless; hidden from assistive tech. */}
            <span aria-hidden className="contents">
              {message}
              <i aria-hidden className="dot" />
            </span>
          </div>
        </div>

      </div>
    </>
  );
}
